import type { FC } from 'react';
import React, { useCallback } from 'react';
import Slider from 'react-slick';
import { Box, Flex } from '@withjoy/joykit';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { useOptimizedEventPhoto } from '@apps/guest/packages/layout-engine/hooks/useOptimizedEventPhoto';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { SliderContainer } from './PhotoSlider.styles';
import type { Photo, AlohaPhotoSliderProps } from './PhotoSlider.types';
import { CroppedImage } from '@shared/components/MultiPhotoSelector/components/CroppedImage/CroppedImage';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { useSliderSettings } from './hooks/useSliderSettings';
import { PHOTO_COUNT_TO_SHOW, ASPECT_RATIO } from './PhotoSlider.constants';
import { useSkipInitialAnimation } from '@apps/guest/packages/layout-engine/hooks/useSkipInitialAnimation';

export const AlohaPhotoSlider: FC<AlohaPhotoSliderProps> = props => {
  const { photos, photoHeight, showPlaceholder } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, showPlaceholder && photos.length === 0);
  const skipAnimation = useSkipInitialAnimation();
  const getPhotoUrl = useOptimizedEventPhoto();

  const [containerDimensions, containerRef] = useSizeObserver();
  const { settings, sliderRef } = useSliderSettings();

  const getPhotoComponent = useCallback(
    (photo: Photo) => {
      if (photo.cropInfo) {
        if (skipAnimation) {
          // SSR path: use background-image with cover instead of CroppedImage's
          // <img> approach. The Aloha slider container is full-viewport (rectangular),
          // but the canvas uses a square container — the <img> approach stretches.
          // Cover with crop-centered position avoids distortion.
          // SSR path: intermediate div replicates the canvas's square container
          // (Math.max(w,h) for both dimensions). The img inside uses percentage-based
          // positioning for exact crop — same approach as CroppedImage. Since the
          // intermediate div is square, both percentage dimensions resolve equally,
          // avoiding the squishing that occurs in rectangular containers.
          const { x, y, width: cw, height: ch } = photo.cropInfo;
          return (
            <Box
              key={photo.id}
              width="100%"
              height={photoHeight || '100%'}
              overflow="hidden"
              position="relative"
            >
              <Box
                position="absolute"
                top="50%"
                left="50%"
                minWidth="100%"
                minHeight="100%"
                overflow="hidden"
                style={{
                  aspectRatio: '1',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <Box
                  as="img"
                  src={getPhotoUrl(photo.photoUrl)}
                  alt=""
                  position="absolute"
                  style={{
                    width: `${10000 / cw}%`,
                    height: `${10000 / ch}%`,
                    left: `${-(x / cw) * 100}%`,
                    top: `${-(y / ch) * 100}%`,
                  }}
                />
              </Box>
            </Box>
          );
        }
        return (
          <Box
            key={photo.id}
            width="100%"
            height="100%"
          >
            <Flex
              width="100%"
              height={photoHeight || '100%'}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <CroppedImage
                width={Math.max(containerDimensions.width, containerDimensions.height)}
                height={Math.max(containerDimensions.width, containerDimensions.height)}
                key={photo.id}
                imageUrl={getPhotoUrl(photo.photoUrl)}
                cropInfo={{ ...photo.cropInfo, unit: '%' }}
                canvasStyles={{ position: 'absolute' }}
              />
            </Flex>
          </Box>
        );
      } else {
        return (
          <Box
            key={photo.id}
            width="100%"
            height={photoHeight || '100%'}
            aspectRatio={{ _: ASPECT_RATIO.mobile, sm2: ASPECT_RATIO.desktop }}
            backgroundImage={backgroundImageForUrl(getPhotoUrl(photo.photoUrl))}
            backgroundSize="cover"
            backgroundRepeat="no-repeat"
            backgroundPosition="center"
          />
        );
      }
    },
    [containerDimensions.height, containerDimensions.width, photoHeight, skipAnimation, getPhotoUrl],
  );

  return (
    <SliderContainer
      ref={containerRef}
      width="100%"
      height="100%"
      position="relative"
    >
      <Slider
        ref={sliderRef}
        {...settings}
      >
        {filledPhotos.map((photo, index) => {
          return photo.photoUrl ? (
            getPhotoComponent(photo)
          ) : (
            <Box
              key={index}
              width="100%"
              height={photoHeight || '100%'}
              aspectRatio={{ _: ASPECT_RATIO.mobile, sm2: ASPECT_RATIO.desktop }}
              backgroundColor="lightgray"
            />
          );
        })}
      </Slider>
    </SliderContainer>
  );
};

AlohaPhotoSlider.displayName = 'AlohaPhotoSlider';
